import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { QuizLength } from '@/types';
import { api } from '@/lib/api';
import { CircularLoader } from './CircularLoader';
import { QuizSettingsModal } from './QuizSettingsModal';
import { useUserTier, useUserProfile } from '@/queries/subscriptions';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useRefreshPendingQuizzes } from '@/hooks/usePendingQuizzes';
import { canGenerateQuiz } from '@/types/subscription';
import type { Difficulty } from '@/types';
import styles from './PromptInput.module.scss';
import HorizontalSlidersIcon from '@/assets/common/sliders-horizontal.svg?react';

interface PromptInputProps {
  onQuizStarted?: () => void;
}

const QUIZ_LENGTH_OPTIONS: Array<{ value: QuizLength; label: string; description: string; count: number }> = [
  { value: 'short', label: 'Short', description: 'Quick review', count: 5 },
  { value: 'intermediate', label: 'Intermediate', description: 'Balanced practice', count: 10 },
  { value: 'comprehensive', label: 'Comprehensive', description: 'In-depth study', count: 20 },
];

export function PromptInput({ onQuizStarted }: PromptInputProps) {
  const [prompt, setPrompt] = useState('');
  const [length, setLength] = useState<QuizLength>('intermediate');
  const [includeMath, setIncludeMath] = useState(false);
  const [distribution, setDistribution] = useState<Record<string, number>>({
    multiple_choice: 6,
    fill_blank: 3,
    mix_match: 1,
    math: 0,
    code: 0
  });
  const [difficulty, setDifficulty] = useState<Difficulty | 'mixed'>('mixed');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { showToast } = useToast();
  const refreshPendingQuizzes = useRefreshPendingQuizzes();

  const maxLength = 500;
  const charCount = prompt.length;
  const charPercentage = (charCount / maxLength) * 100;

  const userTier = useUserTier();
  const isPaidUser = userTier !== 'foundation';

  const { user } = useAuth();
  const { data: userStatus } = useUserProfile();

  // Calculate quota status
  const isQuotaExceeded = !!(user && userStatus?.usage && !canGenerateQuiz(userStatus.usage, userTier));

  const getButtonTooltip = () => {
    if (!user) return "Please log in to generate a quiz";
    if (isQuotaExceeded) return "You have exceeded your monthly quiz generation quota. Please wait for it to reset.";
    return "";
  };

  const isDisabled = !prompt.trim() || isSubmitting || charCount > maxLength || !user || isQuotaExceeded;
  const tooltipText = getButtonTooltip();

  const totalQuestions = QUIZ_LENGTH_OPTIONS.find(opt => opt.value === length)?.count || 10;

  // Update distribution when length changes (if not custom edited, or just keep it simple)
  useEffect(() => {
    // Reset to default balanced ratio for new length
    const mc = Math.round(totalQuestions * 0.6);
    const fib = Math.round(totalQuestions * 0.3);
    const mix = totalQuestions - mc - fib;

    setDistribution({
      multiple_choice: mc,
      fill_blank: fib,
      mix_match: mix,
      math: includeMath ? Math.max(1, Math.round(totalQuestions * 0.1)) : 0,
      code: 0
    });
  }, [length, totalQuestions, includeMath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim() && !isSubmitting && !isDisabled) {
      setIsSubmitting(true);
      setError(null);
      try {
        await api.generateQuiz(
          prompt.trim(),
          length,
          includeMath,
          false, // includeCode
          isPaidUser ? distribution : undefined,
          isPaidUser ? difficulty : undefined
        );

        // Clear the form
        setPrompt('');
        setIsSubmitting(false);

        // Refresh pending quizzes to show the new one
        refreshPendingQuizzes();

        // Show toast notification
        showToast(
          "Quiz is being generated in the background. You'll be notified when it's ready for review.",
          'info',
          6000
        );

        // Call optional callback
        onQuizStarted?.();
      } catch (err) {
        console.log(err);
        setIsSubmitting(false);
        setError('Failed to start quiz generation. Please try again.');
      }
    }
  };

  return (
    <div className={styles.promptInput}>
      <label htmlFor="prompt" className={styles.promptInput__label}>
        What would you like to learn about?
      </label>

      <form onSubmit={handleSubmit} className={styles.promptInput__form}>
        <div className={styles.promptInput__lengthSelector}>
          <div className={styles.promptInput__selectorHeader}>
            <label className={styles.promptInput__lengthLabel}>Quiz Length</label>
            {isPaidUser && (
              <motion.button
                type="button"
                className={styles.promptInput__settingsButton}
                onClick={() => setIsSettingsOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
                title="Advanced Settings"
              >
                <HorizontalSlidersIcon />
              </motion.button>
            )}
          </div>
          <div className={styles.promptInput__lengthOptions}>
            {QUIZ_LENGTH_OPTIONS.map((option) => (
              <motion.button
                key={option.value}
                type="button"
                className={`${styles.promptInput__lengthOption} ${length === option.value ? styles['promptInput__lengthOption--active'] : ''
                  }`}
                onClick={() => setLength(option.value)}
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              >
                <span className={styles.promptInput__lengthOptionLabel}>{option.label}</span>
                <span className={styles.promptInput__lengthOptionCount}>{option.count} questions</span>
                <span className={styles.promptInput__lengthOptionDescription}>{option.description}</span>
              </motion.button>
            ))}
          </div>
        </div>
        <QuizSettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          totalQuestions={totalQuestions}
          initialDistribution={distribution}
          initialDifficulty={difficulty}
          initialIncludeMath={includeMath}
          onSave={(settings) => {
            setDistribution(settings.distribution);
            setDifficulty(settings.difficulty);
            setIncludeMath(settings.includeMath);
          }}
        />

        <motion.textarea
          id="prompt"
          className={styles.promptInput__textarea}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g., DNA replication, Newton's second law, plate tectonics, or photosynthesis light reactions"          maxLength={maxLength}
          disabled={isSubmitting}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        />

        <div className={styles.promptInput__meta}>
          <span
            className={`${styles.promptInput__charCount} ${charPercentage > 90
              ? styles['promptInput__charCount--error']
              : charPercentage > 75
                ? styles['promptInput__charCount--warning']
                : ''
              }`}
          >
            {charCount} / {maxLength}
          </span>
        </div>

        <div title={tooltipText} style={{ width: '100%' }}>
          <motion.button
            type="submit"
            className={styles.promptInput__button}
            disabled={isDisabled}
            style={{
              opacity: isDisabled && (isQuotaExceeded || !user) ? 0.6 : 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer'
            }}
            whileHover={!isDisabled ? { scale: 1.02 } : {}}
            whileTap={!isDisabled ? { scale: 0.98 } : {}}
          >
            <span className={styles.promptInput__buttonContent}>
              {isSubmitting && <CircularLoader className={styles.promptInput__loader} />}
              {isSubmitting ? 'Starting...' : 'Generate Quiz'}
            </span>
          </motion.button>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              className={styles.promptInput__error}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>
      </form >
    </div >
  );
}
